import React, { useState } from "react";

import { useQuery } from "@tanstack/react-query";
import * as yup from "yup";

import AccessComponent from "../../../../../../components/Generic/Access";
import ActionButtons from "../../../../../../components/Generic/ActionButtons";
import LoadingModal from "../../../../../../components/LoadingModal";
import { ExternalList } from "../../../../../../context/MovingResources";
import { useAppState } from "../../../../../../context/StateContext";
import { Options } from "../../../../../../models/access";
import { DifferentLocation } from "../../../../../../models/location";
import { Order } from "../../../../../../models/order";
import AccessService from "../../../../../../services/access.service";
import { Status } from "../../../../../../util/enums";
import useStyles from "./styles";
import { MOVING_AGENT_COMPANY_PROFILE_ID } from "Constants";

type Address = {
  origin: DifferentLocation[];
  destination: DifferentLocation[];
};

interface Props {
  data?: Order;
  addresses: Address;
  handleUpdate: (attribute: string, value: string) => void;
  onCancel: () => void;
  onSave: () => void;
  onSavePublish?: () => void;
  isUpdating: boolean;
  errors?: yup.ValidationError[];
  viewMode?: boolean;
  externalList?: ExternalList;
}

const AccessTab = ({
  data,
  addresses: _addresses,
  handleUpdate,
  onCancel,
  onSave,
  onSavePublish,
  isUpdating,
  errors,
  viewMode,
  externalList,
}: Props) => {
  const { currentUser, agentPermissions } = useAppState();
  const classes = useStyles();

  const [drivewayLength, setDrivewayLength] = useState<Options[]>(
    externalList?.drivewayLengthList || []
  );
  const [parkings, setParkings] = useState<Options[]>(externalList?.parkingList || []);
  const [carryDistance, setCarryDistance] = useState<Options[]>(
    externalList?.carryDistanceList || []
  );
  const [accessActions, setAccessActions] = useState<Options[]>(
    externalList?.accessActionList || []
  );
  const [drivewayList, setDrivewayList] = useState<Options[]>(externalList?.drivewayList || []);

  useQuery(
    ["driveway.length.options"],
    () =>
      AccessService.getAllDrivewayLengths({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        sortBy: JSON.stringify([["id", "ASC"]]),
      }),
    {
      enabled: !externalList?.drivewayLengthList,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setDrivewayLength(response.data);
      },
    }
  );

  useQuery(
    ["parkings"],
    () =>
      AccessService.getAllParkings({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
      }),
    {
      enabled: !externalList?.parkingList,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) {
          setParkings(response.data);
        }
      },
    }
  );

  useQuery(
    ["carryDistance"],
    () =>
      AccessService.getAllCarryDistances({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
      }),
    {
      enabled: !externalList?.carryDistanceList,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) {
          setCarryDistance(response.data);
        }
      },
    }
  );

  useQuery(
    ["accessActions"],
    () =>
      AccessService.getAllActions({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
      }),
    {
      enabled: !externalList?.accessActionList,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) {
          setAccessActions(response.data);
        }
      },
    }
  );

  useQuery(
    ["driveways"],
    () =>
      AccessService.getAllDriveways({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
      }),
    {
      enabled: !externalList?.drivewayList,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) {
          setDrivewayList(response.data);
        }
      },
    }
  );

  return (
    <div data-cy="accessComponentBox">
      <div className={classes.container}>
        <AccessComponent
          data={data}
          handleUpdate={handleUpdate}
          selectOptions={{
            drivewayLength,
            parkings,
            carryDistance,
            accessActions,
            drivewayList,
          }}
          errors={errors}
          viewMode={
            viewMode ||
            !(
              currentUser?.company.profileId !== MOVING_AGENT_COMPANY_PROFILE_ID ||
              agentPermissions?.[Number(data?.id)]?.order?.sections?.access.fields.general.edit === true
            )
          }
        />
        <div className={classes.buttons}>
          <ActionButtons
            cancelDisabled={isUpdating}
            saveDisabled={isUpdating}
            onCancel={onCancel}
            onSavePublish={!isUpdating ? onSavePublish : undefined}
            onSave={onSave}
          />
        </div>
      </div>
      <LoadingModal showing={isUpdating} />
    </div>
  );
};

export default AccessTab;
